import { forwardRef, useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Building, Search } from "lucide-react";
import { useEmpresasResponsaveis, type EmpresaResponsavel } from "@/hooks/useEmpresasResponsaveis";

interface EmpresaSearchSelectProps {
  value: string;
  onChange: (empresaId: string, empresa: EmpresaResponsavel | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const EmpresaSearchSelect = forwardRef<HTMLInputElement, EmpresaSearchSelectProps>(
  (
    {
      value,
      onChange,
      label = "Empresa (cadastro)",
      placeholder = "Digite nome, CNPJ, telefone ou e-mail...",
      disabled,
    },
    ref
  ) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [selectedName, setSelectedName] = useState("");

    const { data: empresas, isLoading } = useEmpresasResponsaveis();

    useEffect(() => {
      if (value && empresas) {
        const empresa = empresas.find((e) => e.id.toString() === value);
        if (empresa) {
          setSelectedName(empresa.nome);
          setSearchTerm(empresa.nome);
          return;
        }
      }
      setSelectedName("");
      setSearchTerm("");
    }, [value, empresas]);

    const filtered = useMemo(() => {
      const s = searchTerm.trim().toLowerCase();
      if (!empresas) return [];
      if (!s) return empresas;
      return empresas.filter((e) => {
        return (
          e.nome.toLowerCase().includes(s) ||
          (e.cnpj || "").toLowerCase().includes(s) ||
          (e.telefone || "").toLowerCase().includes(s) ||
          (e.email || "").toLowerCase().includes(s) ||
          e.id.toString().includes(s)
        );
      });
    }, [empresas, searchTerm]);

    const handleSelect = (empresa: EmpresaResponsavel) => {
      onChange(empresa.id.toString(), empresa);
      setSelectedName(empresa.nome);
      setSearchTerm(empresa.nome);
      setIsOpen(false);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setSearchTerm(newValue);
      setIsOpen(true);
      if (newValue === "") {
        onChange("", null);
        setSelectedName("");
      }
    };

    const handleInputFocus = () => {
      setIsOpen(true);
      if (searchTerm === "" && selectedName !== "") {
        setSearchTerm("");
      }
    };

    return (
      <div className="relative">
        <Label htmlFor="empresa-search">{label}</Label>
        <div className="relative mt-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            ref={ref}
            id="empresa-search"
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={isLoading ? "Carregando empresas..." : placeholder}
            className="pl-10 w-full"
            disabled={disabled || isLoading}
          />
        </div>

        {isOpen && filtered.length > 0 && (
          <Card className="absolute z-50 w-full mt-1 max-h-60 overflow-y-auto border shadow-lg bg-white">
            <div className="p-2">
              {filtered.map((empresa) => (
                <div
                  key={empresa.id}
                  onClick={() => handleSelect(empresa)}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer rounded-md border-b last:border-b-0"
                >
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <Building className="h-4 w-4 text-green-700" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-green-700 bg-green-50 px-2 py-1 rounded border">
                        {empresa.id}
                      </span>
                      <span className="font-medium text-gray-900">{empresa.nome}</span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {empresa.cnpj && <span className="text-gray-500">{empresa.cnpj}</span>}
                      {empresa.telefone && <span className="ml-2 text-gray-500">{empresa.telefone}</span>}
                      {empresa.email && <span className="ml-2 text-gray-500">{empresa.email}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {isOpen && searchTerm && filtered.length === 0 && !isLoading && (
          <Card className="absolute z-50 w-full mt-1 border shadow-lg bg-white">
            <div className="p-4 text-center text-gray-500">Nenhuma empresa encontrada</div>
          </Card>
        )}
      </div>
    );
  }
);

EmpresaSearchSelect.displayName = "EmpresaSearchSelect";

